'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense, useLayoutEffect, useState, useSyncExternalStore } from 'react';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@clerk/nextjs';
import { SignupForm } from '@lib/auth/components/signup-form';
import { CLAIM_ACCOUNT_EMAIL_PARAM } from '@lib/auth/constants/claim-account-email-param';
import { REAUTH_REQUIRED_PARAM } from '@lib/auth/constants/reauth-required-param';
import { useClerkStatus } from '@lib/auth/hooks/use-clerk-degraded';
import { useClerkKillSwitch } from '@lib/auth/hooks/use-clerk-kill-switch';
import { sanitizeRedirectUrl } from '@lib/auth/utils/sanitize-redirect-url';
import { isOrgInviteJoinRedirect, resolvePostSignupRedirect } from '@lib/org-invite-links/utils/resolve-post-signup-redirect';
import { z } from 'zod';
import { AuthUnavailable } from '@/app/(public)/(auth)/_components/auth-unavailable';
import { GoogleOneTapPrompt } from '@/app/(public)/(auth)/_components/google-one-tap-prompt';
const claimAccountEmailSchema = z.string().email();

/** Returns a valid account-claim email without trusting arbitrary query input. */
function parseClaimAccountEmail(searchParams: URLSearchParams): string | undefined {
  const raw = searchParams.get(CLAIM_ACCOUNT_EMAIL_PARAM);
  if (!raw) {
    return undefined;
  }
  const result = claimAccountEmailSchema.safeParse(raw);
  return result.success ? result.data : undefined;
}
const subscribeToHydration = () => () => undefined;
const getHydratedSnapshot = () => true;
const getServerSnapshot = () => false;
function useHasHydrated() {
  return useSyncExternalStore(subscribeToHydration, getHydratedSnapshot, getServerSnapshot);
}
function SignUpLoadingState() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div><Image src="/assets/images/logo.png" className="mx-auto mb-8 h-8 w-8" width={32} height={32} alt="logo" data-sentry-element="Image" data-sentry-source-file="page.tsx" /><div className="bg-muted mx-auto h-7 w-24 animate-pulse rounded md:h-8" /><div className="bg-muted mx-auto mt-2 h-4 w-48 animate-pulse rounded" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="bg-muted h-10 w-full animate-pulse rounded-lg" />;
    t2 = <div className="bg-muted mx-auto h-5 w-32 animate-pulse rounded" />;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="relative mx-auto flex w-full max-w-md flex-col justify-center px-4 py-6 md:px-8 md:py-0" data-sentry-component="SignUpLoadingState" data-sentry-source-file="page.tsx"><div className="w-full max-w-md space-y-8">{t0}<div className="space-y-4">{t1}{t2}<div className="space-y-2"><div className="bg-muted h-10 w-full animate-pulse rounded-lg" /><div className="bg-muted h-10 w-full animate-pulse rounded-lg" /><div className="bg-muted h-10 w-full animate-pulse rounded-lg" /></div><div className="bg-muted h-10 w-full animate-pulse rounded-lg" /></div></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function SignUpPageContent() {
  const $ = _c(36);
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectedFrom = sanitizeRedirectUrl(searchParams.get("redirectedFrom"));
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get(REAUTH_REQUIRED_PARAM);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const reauthRequired = t0 != null;
  let t1;
  if ($[2] !== searchParams) {
    t1 = parseClaimAccountEmail(searchParams);
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const claimAccountEmail = t1;
  const isClaimAccount = Boolean(claimAccountEmail);
  const {
    isDegraded: isClerkDegraded,
    isClerkLoading
  } = useClerkStatus();
  const clerkKillSwitch = useClerkKillSwitch();
  const hasHydrated = useHasHydrated();
  const {
    isLoaded: isAuthLoaded,
    isSignedIn
  } = useAuth();
  const [isClaimingShellAccount, setIsClaimingShellAccount] = useState(false);
  const postSignupHref = resolvePostSignupRedirect(redirectedFrom);
  let t2;
  let t3;
  if ($[4] !== hasHydrated || $[5] !== isAuthLoaded || $[6] !== isClaimingShellAccount || $[7] !== isClerkLoading || $[8] !== isSignedIn || $[9] !== postSignupHref || $[10] !== reauthRequired || $[11] !== router) {
    t2 = () => {
      if (!hasHydrated || isClerkLoading || !isAuthLoaded || !isSignedIn || isClaimingShellAccount || reauthRequired) {
        return;
      }
      router.replace(postSignupHref);
    };
    t3 = [hasHydrated, isClerkLoading, isAuthLoaded, isSignedIn, isClaimingShellAccount, reauthRequired, postSignupHref, router];
    $[4] = hasHydrated;
    $[5] = isAuthLoaded;
    $[6] = isClaimingShellAccount;
    $[7] = isClerkLoading;
    $[8] = isSignedIn;
    $[9] = postSignupHref;
    $[10] = reauthRequired;
    $[11] = router;
    $[12] = t2;
    $[13] = t3;
  } else {
    t2 = $[12];
    t3 = $[13];
  }
  useLayoutEffect(t2, t3);
  if (!hasHydrated || isClerkLoading) {
    let t4;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <SignUpLoadingState />;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    return t4;
  }
  if (clerkKillSwitch || isClerkDegraded) {
    let t4;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <AuthUnavailable action="Sign-up" />;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    return t4;
  }
  let t4;
  if ($[16] !== isClaimingShellAccount || $[17] !== postSignupHref) {
    t4 = !isClaimingShellAccount && <GoogleOneTapPrompt signInRedirectUrl={postSignupHref} signUpRedirectUrl={postSignupHref} />;
    $[16] = isClaimingShellAccount;
    $[17] = postSignupHref;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Image src="/assets/images/logo.png" className="mx-auto mb-8 h-8 w-8" width={32} height={32} alt="logo" data-sentry-element="Image" data-sentry-source-file="page.tsx" />;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = isClaimAccount ? "Claim Account" : "Sign Up";
  let t7;
  if ($[20] !== t6) {
    t7 = <h1 className="font-ivar text-center text-xl font-normal text-gray-800 md:text-[28px]">{t6}</h1>;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="mt-2 text-center text-xs text-gray-700 md:text-sm">We are excited to have you join The Clai Club.</p>;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7) {
    t9 = <div>{t5}{t7}{t8}</div>;
    $[23] = t7;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== claimAccountEmail || $[26] !== postSignupHref || $[27] !== redirectedFrom || $[28] !== setIsClaimingShellAccount) {
    t10 = <SignupForm redirectedFrom={redirectedFrom} postSignupHref={postSignupHref} lockedEmail={claimAccountEmail} onShellClaimActiveChange={setIsClaimingShellAccount} data-sentry-element="SignupForm" data-sentry-source-file="page.tsx" />;
    $[25] = claimAccountEmail;
    $[26] = postSignupHref;
    $[27] = redirectedFrom;
    $[28] = setIsClaimingShellAccount;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t10 || $[31] !== t9) {
    t11 = <div className="w-full max-w-md space-y-8">{t9}{t10}</div>;
    $[30] = t10;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t11 || $[34] !== t4) {
    t12 = <div className="relative mx-auto flex w-full max-w-md flex-col justify-center px-4 py-6 md:px-8 md:py-0" data-sentry-component="SignUpPageContent" data-sentry-source-file="page.tsx">{t4}{t11}</div>;
    $[33] = t11;
    $[34] = t4;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
}
export default function SignUpPage() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SignUpLoadingState />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Suspense fallback={t0} data-sentry-element="Suspense" data-sentry-component="SignUpPage" data-sentry-source-file="page.tsx"><SignUpPageContent data-sentry-element="SignUpPageContent" data-sentry-source-file="page.tsx" /></Suspense>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}